import { Mountain, ShieldCheck, Smartphone, Trash2, UserRound } from 'lucide-react'

const deletionSteps = [
  {
    icon: Smartphone,
    title: 'Open HikeStrong on your phone',
    description: 'Sign in to the account you want to delete.'
  },
  {
    icon: UserRound,
    title: 'Go to the Profile screen',
    description: 'Tap Profile in the app navigation, then scroll to the account actions.'
  },
  {
    icon: Trash2,
    title: 'Tap Delete Account',
    description: 'Review the warning and confirm the permanent deletion.'
  }
]

export const AccountDeletionPage = () => (
  <main className="min-h-screen bg-background topo-pattern px-5 py-10 sm:px-8 sm:py-14">
    <div className="mx-auto w-full max-w-3xl">
      <header className="mb-8 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-lg">
          <Mountain className="h-6 w-6" strokeWidth={2.5} aria-hidden="true" />
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-accent">HikeStrong</p>
          <p className="text-xl font-bold tracking-tight text-foreground">Base Camp</p>
        </div>
      </header>

      <section className="elevation-card overflow-hidden rounded-2xl">
        <div className="border-b border-border bg-primary px-6 py-8 text-primary-foreground sm:px-10">
          <p className="font-mono text-xs font-bold uppercase tracking-wider text-accent">
            Account controls
          </p>
          <h1 className="mt-3 text-3xl font-bold sm:text-4xl">Delete your HikeStrong account</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-primary-foreground/80 sm:text-base">
            You can permanently delete your account and associated app data from the HikeStrong mobile app.
          </p>
        </div>

        <div className="space-y-9 px-6 py-8 sm:px-10 sm:py-10">
          <section aria-labelledby="deletion-steps-heading">
            <h2 id="deletion-steps-heading" className="text-xl font-bold text-foreground">
              Delete your account in the app
            </h2>
            <ol className="mt-5 grid gap-4">
              {deletionSteps.map((step, index) => {
                const Icon = step.icon
                return (
                  <li key={step.title} className="flex gap-4 rounded-xl border border-border bg-card p-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">
                        {index + 1}. {step.title}
                      </p>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">{step.description}</p>
                    </div>
                  </li>
                )
              })}
            </ol>
          </section>

          <section className="rounded-xl border border-border bg-muted/50 p-5" aria-labelledby="deleted-data-heading">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              <div>
                <h2 id="deleted-data-heading" className="font-bold text-foreground">What deletion includes</h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Confirming deletion permanently removes your HikeStrong profile and associated app data, including
                  training plans, workout history, uploaded media, social content, messages, and support data. The
                  deleted account cannot be recovered.
                </p>
              </div>
            </div>
          </section>

          <section aria-labelledby="subscription-heading">
            <h2 id="subscription-heading" className="text-lg font-bold text-foreground">Store subscriptions</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Deleting your HikeStrong account does not cancel an App Store or Google Play subscription. Cancel any
              active subscription in your store account settings before deleting your account. Apple and Google may
              retain transaction records under their own policies.
            </p>
          </section>

          <section className="border-t border-border pt-7" aria-labelledby="help-heading">
            <h2 id="help-heading" className="text-lg font-bold text-foreground">Cannot access the app?</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Email <a className="font-semibold text-primary hover:underline" href="mailto:admin@hikestrongapp.com?subject=Account%20deletion%20request">admin@hikestrongapp.com</a> from
              the email address associated with your account and request account deletion. We will verify account
              ownership before processing the request.
            </p>
          </section>

          <footer className="flex flex-wrap gap-x-5 gap-y-2 border-t border-border pt-6 text-sm text-muted-foreground">
            <a className="font-semibold text-primary hover:underline" href="https://hikestrongapp.com/privacy">
              Privacy Policy
            </a>
            <a className="font-semibold text-primary hover:underline" href="https://hikestrongapp.com/terms">
              Terms of Service
            </a>
            <span>© 2026 HikeStrong</span>
          </footer>
        </div>
      </section>
    </div>
  </main>
)
